import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Paper, Typography, Grid, Button, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Checkbox, CircularProgress, Alert,
  Divider, Autocomplete, Chip, ToggleButton, ToggleButtonGroup,
  Tooltip, useTheme, Stepper, Step, StepLabel
} from '@mui/material';
import {
  Check as CheckIcon, Receipt as ReceiptIcon,
  AccountBalanceWallet as WalletIcon, AutoMode as AutoIcon, Tune as CustomIcon,
  Warning as WarningIcon, NavigateNext as NextIcon, NavigateBefore as PrevIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import SettlementService from '../../services/settlement.service';
import ApiService from '../../services/api.service';
import useOrganizationSearch from '../../hooks/useOrganizationSearch';
import { ProTableStats } from '../../components/pro-table';
import { getBrandedTableHeadSx } from '../../utils/colorUtils';

const fontSize = {
  xs: '0.75rem',
  sm: '0.8125rem',
  md: '0.875rem',
};

const STEPS = ['Select Customer', 'Select Invoices', 'Confirm'];

const CreateSettlement = ({ currentUser }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [activeStep, setActiveStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Step 1: Customer
  const {
    options: organizations,
    loading: loadingOrganizations,
    onInputChange: onOrganizationInputChange,
    filterOptions: organizationFilterOptions
  } = useOrganizationSearch({ typingCenterId: currentUser?.typingCenterId });
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [walletBalance, setWalletBalance] = useState(null);
  const [loadingWallet, setLoadingWallet] = useState(false);

  // Step 2: Mode + Invoices
  const [mode, setMode] = useState('AUTO');
  const [outstandingInvoices, setOutstandingInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);
  const [customAmounts, setCustomAmounts] = useState({}); // { [invoiceId]: amount }


  const handleCustomerChange = async (event, newValue) => {
    setSelectedOrg(newValue);
    setSelectedInvoiceIds([]);
    setOutstandingInvoices([]);
    setWalletBalance(null);

    if (newValue) {
      setLoadingWallet(true);
      setLoadingInvoices(true);
      try {
        const [balance, invoices] = await Promise.all([
          SettlementService.getCustomerWalletBalance(newValue.id),
          SettlementService.getOutstandingInvoices(newValue.id)
        ]);
        setWalletBalance(balance);
        setOutstandingInvoices(invoices);
      } catch (err) {
        setError(err?.response?.data?.message || 'Failed to load customer data');
      } finally {
        setLoadingWallet(false);
        setLoadingInvoices(false);
      }
    }
  };

  // -- Single allocator (oldest-first, single-partial-tail) used by
  //    both AUTO (over all outstanding) and CUSTOM (over selected
  //    invoice ids). Returns { [invoiceId]: payAmount } for the
  //    invoices that get any money. --
  const allocations = useMemo(() => {
    if (walletBalance == null) return {};
    const scope =
      mode === 'AUTO'
        ? outstandingInvoices
        : outstandingInvoices.filter((inv) => selectedInvoiceIds.includes(inv.id));
    const sorted = [...scope].sort(
      (a, b) => new Date(a.invoiceDate) - new Date(b.invoiceDate),
    );
    let remaining = walletBalance;
    const out = {};
    for (const inv of sorted) {
      if (remaining <= 0) break;
      const due = inv.balanceAmount || 0;
      if (due <= 0) continue;
      // In CUSTOM mode, use user-entered amount if provided
      const userAmount = mode === 'CUSTOM' && customAmounts[inv.id] != null
        ? Math.min(customAmounts[inv.id], due)
        : due;
      const pay = Math.min(userAmount, remaining);
      if (pay > 0) {
        out[inv.id] = Math.round(pay * 100) / 100;
        remaining = Math.round((remaining - pay) * 100) / 100;
      }
    }
    return out;
  }, [mode, outstandingInvoices, selectedInvoiceIds, walletBalance, customAmounts]);

  const getPayAmount = (invId) => allocations[invId] || 0;

  // -- Total being settled --
  const totalSettling = useMemo(() => {
    return Object.values(allocations).reduce((sum, v) => sum + v, 0);
  }, [allocations]);

  const walletAfter = walletBalance != null
    ? Math.round((walletBalance - totalSettling) * 100) / 100
    : 0;

  // -- CUSTOM mode: detect "over-selection" (a selected invoice that
  //    couldn't be funded — backend would reject the same way). --
  const unfundedSelected = useMemo(() => {
    if (mode !== 'CUSTOM') return [];
    return selectedInvoiceIds.filter((id) => !(id in allocations));
  }, [mode, selectedInvoiceIds, allocations]);

  const isOverSelected = unfundedSelected.length > 0;

  // -- Lock rule for CUSTOM mode: once the wallet would be exhausted
  //    by the current selection (i.e. a partial-tail row exists),
  //    further unchecked rows lock. --
  const isCustomLocked = useMemo(() => {
    if (mode !== 'CUSTOM') return false;
    if (walletBalance == null) return false;
    // wallet exhausted = no remaining balance after current allocation
    return totalSettling >= walletBalance && selectedInvoiceIds.length > 0;
  }, [mode, walletBalance, totalSettling, selectedInvoiceIds]);

  // -- Invoice selection (CUSTOM mode only; AUTO uses all outstanding) --
  const handleInvoiceToggle = (invId) => {
    setSelectedInvoiceIds((prev) =>
      prev.includes(invId) ? prev.filter((id) => id !== invId) : [...prev, invId],
    );
  };

  const handleSelectAll = () => {
    setSelectedInvoiceIds(
      selectedInvoiceIds.length === outstandingInvoices.length
        ? []
        : outstandingInvoices.map((i) => i.id),
    );
  };

  const getStatusAfter = (inv) => {
    const pay = getPayAmount(inv.id);
    if (pay <= 0) return null;
    if (pay >= inv.balanceAmount) return 'PAID';
    return 'PARTIAL';
  };

  // -- Which invoices will actually receive money (allocator output) --
  const settledInvoices = useMemo(() => {
    return outstandingInvoices
      .filter((inv) => allocations[inv.id] > 0)
      .sort((a, b) => new Date(a.invoiceDate) - new Date(b.invoiceDate));
  }, [outstandingInvoices, allocations]);

  const receiptsToCreate = settledInvoices.length;

  // -- Validation --
  const canProceedStep1 = selectedOrg && walletBalance != null && walletBalance > 0;
  const canProceedStep2 =
    settledInvoices.length > 0 &&
    !isOverSelected &&
    totalSettling > 0;

  const handleCustomAmountChange = (invId, value) => {
    const num = parseFloat(value);
    if (value === '' || value === undefined) {
      setCustomAmounts((prev) => { const n = { ...prev }; delete n[invId]; return n; });
    } else if (!isNaN(num) && num >= 0) {
      setCustomAmounts((prev) => ({ ...prev, [invId]: num }));
    }
  };

  // -- Mode change resets selections --
  const handleModeChange = (e, newMode) => {
    if (newMode) {
      setMode(newMode);
      setSelectedInvoiceIds([]);
      setCustomAmounts({});
    }
  };

  // -- Submit --
  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      const settlementData = {
        customerId: selectedOrg.id,
        mode,
        invoiceIds: mode === 'CUSTOM' ? selectedInvoiceIds : undefined,
        customAmounts: mode === 'CUSTOM' && Object.keys(customAmounts).length > 0
          ? Object.fromEntries(
              Object.entries(customAmounts).filter(([id]) => selectedInvoiceIds.includes(id))
            )
          : undefined,
        notes: null,
      };
      await SettlementService.create(settlementData);
      setSuccess('Settlement created successfully');
      setTimeout(() => navigate('/settlements'), 1500);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to create settlement');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (date) => date ? new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';

  // ============================================================
  // STEP 1: Select Customer
  // ============================================================
  const renderStep1 = () => {
    const hasFunds = walletBalance > 0;
    return (
      <Paper sx={{ p: 2 }}>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'secondary.main', mb: 1.5 }}>Select Customer</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch', flexWrap: 'wrap' }}>
          <Autocomplete
            size="small"
            options={organizations}
            getOptionLabel={(o) => o?.name || ''}
            isOptionEqualToValue={(o, v) => o?.id === v?.id}
            value={selectedOrg}
            onChange={handleCustomerChange}
            onInputChange={onOrganizationInputChange}
            filterOptions={organizationFilterOptions}
            loadingText="Searching..."
            noOptionsText="No organizations found"
            loading={loadingOrganizations}
            ListboxProps={{ sx: { '& .MuiAutocomplete-option': { fontSize: '0.8125rem' } } }}
            renderInput={(params) => (
              <TextField {...params} label="Customer Organization" placeholder="Search by name or TRN..." autoFocus
                helperText={selectedOrg ? ' ' : 'Pick the customer whose wallet pays the invoices.'}
                sx={{ '& .MuiInputBase-input': { fontSize: '0.8125rem' }, '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
                  '& .MuiFormHelperText-root': { fontSize: '0.6875rem' } }} />
            )}
            sx={{ flex: '1 1 320px', maxWidth: 560 }}
          />

          {/* Wallet balance — compact, next to the customer */}
          {selectedOrg && (
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 0.75, height: 40, borderRadius: 1,
              border: '1px solid', borderColor: hasFunds ? 'success.light' : 'error.light',
              bgcolor: hasFunds ? (isDark ? 'rgba(76,175,80,0.10)' : '#f1f8f2') : (isDark ? 'rgba(244,67,54,0.10)' : '#fdf1f0'),
            }}>
              {loadingWallet ? (
                <CircularProgress size={18} />
              ) : (
                <>
                  <WalletIcon sx={{ fontSize: 20, color: hasFunds ? 'success.main' : 'error.main' }} />
                  <Box>
                    <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', lineHeight: 1.2 }}>Wallet balance</Typography>
                    <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, lineHeight: 1.2, color: hasFunds ? 'success.main' : 'error.main' }}>
                      {SettlementService.formatCurrency(walletBalance || 0)}
                    </Typography>
                  </Box>
                  {!hasFunds && walletBalance != null && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                      <WarningIcon sx={{ fontSize: 14, color: 'error.main' }} />
                      <Typography sx={{ fontSize: '0.75rem', color: 'error.main' }}>No funds to settle</Typography>
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}
        </Box>
      </Paper>
    );
  };

  // ============================================================
  // STEP 2: Mode + Invoices
  // ============================================================
  const renderStep2 = () => (
    <>
      {/* Wallet summary first — same tiles as Work Orders */}
      <ProTableStats
        cards={[
          { label: 'Wallet Balance', value: SettlementService.formatCurrency(walletBalance), color: 'success.main' },
          { label: 'Settling', value: SettlementService.formatCurrency(totalSettling), color: 'warning.main' },
          { label: 'Wallet After', value: SettlementService.formatCurrency(walletAfter),
            color: walletAfter > 0 ? 'success.main' : (walletAfter === 0 ? 'warning.main' : 'error.main') },
        ]}
      />

      {/* Mode Toggle */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Box>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'secondary.main' }}>Settlement Mode</Typography>
            <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>
              {mode === 'AUTO'
                ? 'Auto pays all outstanding invoices oldest-first until the wallet is exhausted (last one may be partial; rest are skipped).'
                : 'Pick the invoices to settle and optionally enter a custom amount for partial payments.'}
            </Typography>
          </Box>
          <ToggleButtonGroup value={mode} exclusive onChange={handleModeChange} size="small"
            sx={{ '& .MuiToggleButton-root.Mui-selected': { bgcolor: 'primary.main', color: 'primary.contrastText', '&:hover': { bgcolor: 'primary.dark' } },
              '& .MuiToggleButton-root.Mui-selected .MuiSvgIcon-root': { color: 'primary.contrastText' } }}>
            <ToggleButton value="AUTO" sx={{ fontSize: '0.8125rem', textTransform: 'none', px: 1.5, height: 32 }}>
              <AutoIcon sx={{ fontSize: 16, mr: 0.5 }} /> Auto
            </ToggleButton>
            <ToggleButton value="CUSTOM" sx={{ fontSize: '0.8125rem', textTransform: 'none', px: 1.5, height: 32 }}>
              <CustomIcon sx={{ fontSize: 16, mr: 0.5 }} /> Custom
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Paper>

      {isOverSelected && (
        <Alert severity="error" sx={{ mb: 2, fontSize: fontSize.sm }}>
          The wallet can't cover all selected invoices. {unfundedSelected.length}{' '}
          selected invoice{unfundedSelected.length === 1 ? '' : 's'} would be unfunded.
          Uncheck the extras to proceed.
        </Alert>
      )}

      {/* Invoice Table */}
      <Paper sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'secondary.main' }}>Outstanding Invoices ({outstandingInvoices.length})</Typography>
        </Box>

        {loadingInvoices ? <Box display="flex" justifyContent="center" py={4}><CircularProgress size={28} /></Box> :
        outstandingInvoices.length === 0 ? (
          <Box py={4} textAlign="center">
            <ReceiptIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.4, mb: 1 }} />
            <Typography sx={{ fontSize: fontSize.sm, color: 'text.secondary' }}>No outstanding invoices for this customer</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead sx={getBrandedTableHeadSx(theme)}>
                <TableRow>
                  {mode === 'CUSTOM' && (
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={selectedInvoiceIds.length === outstandingInvoices.length && outstandingInvoices.length > 0}
                        indeterminate={selectedInvoiceIds.length > 0 && selectedInvoiceIds.length < outstandingInvoices.length}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                  )}
                  <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary' }}>Invoice #</TableCell>
                  <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary' }}>Date</TableCell>
                  <TableCell align="right" sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary' }}>Amount Due</TableCell>
                  <TableCell align="right" sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary' }}>Will Pay</TableCell>
                  <TableCell align="center" sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary' }}>Status After</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {outstandingInvoices.map((inv) => {
                  const isSelected = mode === 'CUSTOM' ? selectedInvoiceIds.includes(inv.id) : true;
                  const payAmount = getPayAmount(inv.id);
                  const statusAfter = payAmount > 0 ? getStatusAfter(inv) : null;
                  const checkboxLocked =
                    mode === 'CUSTOM' && !isSelected && isCustomLocked;

                  return (
                    <TableRow key={inv.id} hover selected={isSelected}
                      sx={{ opacity: isSelected ? 1 : 0.6 }}>
                      {mode === 'CUSTOM' && (
                        <TableCell padding="checkbox">
                          <Tooltip title={checkboxLocked ? 'Wallet covers up to one partial-tail. Uncheck a selection to add another.' : ''} placement="top">
                            <span>
                              <Checkbox
                                size="small"
                                checked={isSelected}
                                disabled={checkboxLocked}
                                onChange={() => handleInvoiceToggle(inv.id)}
                              />
                            </span>
                          </Tooltip>
                        </TableCell>
                      )}
                      <TableCell sx={{ py: 1 }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'secondary.main' }}>{inv.invoiceNumber}</Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: '13px', py: 1, color: 'text.secondary' }}>{formatDate(inv.invoiceDate)}</TableCell>
                      <TableCell align="right" sx={{ py: 1 }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{SettlementService.formatCurrency(inv.balanceAmount)}</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 1 }}>
                        {mode === 'CUSTOM' && isSelected ? (
                          <TextField
                            size="small"
                            type="number"
                            value={customAmounts[inv.id] != null ? customAmounts[inv.id] : inv.balanceAmount}
                            onChange={(e) => handleCustomAmountChange(inv.id, e.target.value)}
                            inputProps={{ min: 0, max: inv.balanceAmount, step: '0.01' }}
                            sx={{ width: 120, '& input': { fontSize: '13px', fontWeight: 600, color: '#4caf50', textAlign: 'right', py: 0.75 } }}
                          />
                        ) : (
                          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: payAmount > 0 ? '#4caf50' : 'text.secondary' }}>
                            {payAmount > 0 ? SettlementService.formatCurrency(payAmount) : '—'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ py: 1 }}>
                        {statusAfter === 'PAID' && (
                          <Chip icon={<CheckIcon sx={{ fontSize: 14 }} />} label="PAID" size="small" color="success" sx={{ fontSize: fontSize.xs, height: 22 }} />
                        )}
                        {statusAfter === 'PARTIAL' && (
                          <Chip icon={<WarningIcon sx={{ fontSize: 14 }} />} label="PARTIAL" size="small" color="warning" sx={{ fontSize: fontSize.xs, height: 22 }} />
                        )}
                        {!statusAfter && <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>—</Typography>}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {/* Totals row */}
                {settledInvoices.length > 0 && (
                  <TableRow sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.04)' : '#fafafa' }}>
                    <TableCell colSpan={mode === 'CUSTOM' ? 3 : 2} sx={{ py: 1.5 }}>
                      <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>Total ({settledInvoices.length} invoice{settledInvoices.length === 1 ? '' : 's'})</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ py: 0.75 }}>
                      <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                        {SettlementService.formatCurrency(settledInvoices.reduce((s, inv) => s + inv.balanceAmount, 0))}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ py: 0.75 }}>
                      <Typography sx={{ fontSize: '13px', fontWeight: 700, color: isDark ? '#4caf50' : '#2e7d32' }}>
                        {SettlementService.formatCurrency(totalSettling)}
                      </Typography>
                    </TableCell>
                    <TableCell />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </>
  );

  // ============================================================
  // STEP 3: Confirm
  // ============================================================
  const renderStep3 = () => (
    <>
      <Paper sx={{ p: 2.5, mb: 2 }}>
        <Typography sx={{ fontSize: fontSize.md, fontWeight: 600, mb: 2 }}>Settlement Summary</Typography>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Customer</Typography>
            <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600 }}>{selectedOrg?.name}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Mode</Typography>
            {mode === 'AUTO'
              ? <Chip label="Auto (Oldest First)" size="small" color="info" sx={{ fontSize: fontSize.xs, height: 22 }} />
              : <Chip label="Custom Allocation" size="small" color="secondary" sx={{ fontSize: fontSize.xs, height: 22 }} />
            }
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Wallet Before</Typography>
            <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600 }}>{SettlementService.formatCurrency(walletBalance)}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Wallet After</Typography>
            <Typography sx={{ fontSize: fontSize.sm, fontWeight: 700, color: walletAfter > 0 ? '#4caf50' : '#ff9800' }}>
              {SettlementService.formatCurrency(walletAfter)}
            </Typography>
          </Grid>
        </Grid>

        {/* Preview table */}
        <TableContainer>
          <Table size="small">
            <TableHead sx={getBrandedTableHeadSx(theme)}>
              <TableRow>
                <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary' }}>Invoice</TableCell>
                <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary' }}>Date</TableCell>
                <TableCell align="right" sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary' }}>Due</TableCell>
                <TableCell align="right" sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary' }}>Will Pay</TableCell>
                <TableCell align="center" sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary' }}>Status After</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {settledInvoices.map((inv) => {
                const pay = getPayAmount(inv.id);
                const status = getStatusAfter(inv);
                return (
                  <TableRow key={inv.id} hover>
                    <TableCell sx={{ fontSize: '13px', fontWeight: 600, color: 'secondary.main', py: 1 }}>{inv.invoiceNumber}</TableCell>
                    <TableCell sx={{ fontSize: '13px', py: 1, color: 'text.secondary' }}>{formatDate(inv.invoiceDate)}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '13px', py: 1 }}>{SettlementService.formatCurrency(inv.balanceAmount)}</TableCell>
                    <TableCell align="right" sx={{ py: 1 }}>
                      <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#4caf50' }}>{SettlementService.formatCurrency(pay)}</Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1 }}>
                      {status === 'PAID'
                        ? <Chip icon={<CheckIcon sx={{ fontSize: 14 }} />} label="PAID" size="small" color="success" sx={{ fontSize: fontSize.xs, height: 22 }} />
                        : <Chip icon={<WarningIcon sx={{ fontSize: 14 }} />} label="PARTIAL" size="small" color="warning" sx={{ fontSize: fontSize.xs, height: 22 }} />
                      }
                    </TableCell>
                  </TableRow>
                );
              })}
              <TableRow sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.04)' : '#fafafa' }}>
                <TableCell colSpan={2} sx={{ py: 1.5 }}><Typography sx={{ fontSize: '13px', fontWeight: 700 }}>Total</Typography></TableCell>
                <TableCell align="right" sx={{ py: 0.75 }}>
                  <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                    {SettlementService.formatCurrency(settledInvoices.reduce((s, inv) => s + inv.balanceAmount, 0))}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ py: 0.75 }}>
                  <Typography sx={{ fontSize: '13px', fontWeight: 700, color: isDark ? '#4caf50' : '#2e7d32' }}>
                    {SettlementService.formatCurrency(totalSettling)}
                  </Typography>
                </TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

    </>
  );

  // ============================================================
  // MAIN RENDER
  // ============================================================
  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2, fontSize: fontSize.sm }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2, fontSize: fontSize.sm }}>{success}</Alert>}

      {/* Confirm step: totals above the step bar */}
      {activeStep === 2 && (
        <ProTableStats
          cards={[
            { label: 'Total Settlement', value: SettlementService.formatCurrency(totalSettling), color: 'warning.main' },
            { label: 'Receipts to Create', value: receiptsToCreate, color: 'info.main' },
            { label: 'Wallet After', value: SettlementService.formatCurrency(walletAfter), color: walletAfter > 0 ? 'success.main' : 'warning.main' },
          ]}
        />
      )}


      {/* Step Content */}
      {activeStep === 0 && renderStep1()}
      {activeStep === 1 && renderStep2()}
      {activeStep === 2 && renderStep3()}

      {/* Step bar below the content, pinned to the bottom of the screen so Next / Confirm are always reachable */}
      <Paper sx={{ px: 2, py: 1, mt: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', position: 'sticky', bottom: 0, zIndex: 2 }}>
        <Stepper activeStep={activeStep} sx={{ flex: '1 1 360px', '& .MuiStepIcon-root': { fontSize: 20 } }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel sx={{ '& .MuiStepLabel-label': { fontSize: '0.8125rem' } }}>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <Box display="flex" gap={1} alignItems="center" sx={{ ml: 'auto' }}>
          <Button size="small" startIcon={<PrevIcon />}
            onClick={() => activeStep === 0 ? navigate('/settlements') : setActiveStep(prev => prev - 1)}
            sx={{ fontSize: '0.8125rem', textTransform: 'none' }}>
            {activeStep === 0 ? 'Cancel' : 'Back'}
          </Button>
          {activeStep < 2 && (
            <Button variant="contained" size="small" endIcon={<NextIcon />}
              onClick={() => setActiveStep(prev => prev + 1)}
              disabled={(activeStep === 0 && !canProceedStep1) || (activeStep === 1 && !canProceedStep2)}
              sx={{ fontSize: '0.8125rem', textTransform: 'none' }}>
              Next
            </Button>
          )}
          {activeStep === 2 && (
            <Button variant="contained" size="small" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
              onClick={handleSubmit} disabled={saving}
              sx={{ fontSize: '0.8125rem', textTransform: 'none' }}>
              {saving ? 'Creating...' : 'Confirm Settlement'}
            </Button>
          )}
        </Box>
      </Paper>

    </Box>
  );
};

export default CreateSettlement;
